import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import kebabCase from 'lodash/kebabCase';
import without from 'lodash/without';
import union from 'lodash/union';
import partition from 'lodash/partition';

import Form from '../../../../components/Form';
import Checkbox from '../../../../components/Checkbox';
import { transferApplicationRequirementsComparator } from '../../cmsDataMappingHelpers';
import withCollegeService from '../withCollegeService';
import TextFieldList from './components/textFieldList';
import messages from './messages';

import '../styles.css';

const keyFromName = (name) => kebabCase(name);

export const predefinedOptions = [
  'Academic Evaluation',
  'College transcript(s)',
  'College Report',
  'Course Descriptions From College Catalog From Each College/University Attended',
  'Essay or Personal Statement',
  'High School Transcript',
  'Letters of Recommendation',
  'Standardized Test Scores',
];

export const TransferApplicationRequirementsComponent = ({ onChange, initialValue = {} }) => {
  const [selectedPredefinedOptions, customOptions] = useMemo(
    () => partition(initialValue?.value || [], (value) => predefinedOptions.includes(value)),
    [initialValue],
  );
  const [otherCheckboxValue, setOtherCheckboxValue] = useState(customOptions.length > 0);

  const onPredefinedOptionChange = useCallback((e) => {
    const checked = e.target.checked === true;
    const { name } = e.target;
    const newValue = checked ? union(selectedPredefinedOptions, [name]) : without(selectedPredefinedOptions, name);
    onChange(e, {
      ...initialValue,
      value: newValue.sort(transferApplicationRequirementsComparator).concat(customOptions),
    });
  }, [selectedPredefinedOptions, customOptions]);

  const onCustomOptionChange = useCallback((values) => {
    const newValue = selectedPredefinedOptions.concat(values);
    onChange(null, {
      ...initialValue,
      value: newValue,
    });
  }, [selectedPredefinedOptions, customOptions]);

  const onOtherCheckboxChange = useCallback((e) => {
    const checked = !!e.target.checked;
    setOtherCheckboxValue(checked);
  }, [setOtherCheckboxValue]);

  const selectedValues = (initialValue?.value || []).reduce((acc, name) => {
    acc[keyFromName(name)] = true;
    return acc;
  }, {});

  return (
    <div styleName="transfer-application-requirements-container" role="list" data-cy="Premium-Hem-Transfer-Application-Requirements-Container">
      {predefinedOptions.map((name) => {
        const key = keyFromName(name);
        return (
          <Form.Field role="listitem" styleName="field" key={key}>
            <Checkbox
              id={`application-requirements-${key}`}
              label={{
                htmlFor: `application-requirements-${key}`,
                children: name,
              }}
              checked={!!selectedValues[key]}
              name={name}
              onChange={onPredefinedOptionChange}
            />
          </Form.Field>
        );
      })}
      <Checkbox
        data-cy="Premium-Hem-Transfer-Application-Requirements-Other"
        key="other"
        id="application-requirements-other"
        label={{
          htmlFor: 'application-requirements-other',
          children: <FormattedMessage {...messages.applicationRequirementsOther} />,
        }}
        checked={otherCheckboxValue}
        onChange={onOtherCheckboxChange}
      />
      <TextFieldList key="custom-requirements" disabled={!otherCheckboxValue} values={customOptions} onChange={onCustomOptionChange} />
    </div>
  );
};

TransferApplicationRequirementsComponent.propTypes = {
  initialValue: PropTypes.object,
  onChange: PropTypes.func,
};

const TransferApplicationRequirements = withCollegeService(
  TransferApplicationRequirementsComponent,
  'transferInfo.applicationRequirements',
  {},
  {
    value: 'field_he_xfer_admissions_req',
  },
);
export default TransferApplicationRequirements;
